<template>
  <div class="auth-page">
    <div class="auth-card">
      <div class="auth-header">
        <img src="@/assets/img/logo.png" alt="Logo" class="logo" />
        <h1>Registrieren</h1>
        <p>Erstellen Sie ein neues Konto</p>
      </div>
      
      <div class="auth-body">
        <form @submit.prevent="register">
          <div class="alert alert-danger" v-if="error">
            {{ error }}
          </div>
          
          <div class="form-group mb-3">
            <label for="name" class="form-label">Name</label>
            <input 
              type="text" 
              id="name"
              v-model="name"
              class="form-control" 
              required
              autofocus
              placeholder="Max Mustermann"
            />
            <div class="invalid-feedback" v-if="validationErrors.name">
              {{ validationErrors.name[0] }}
            </div>
          </div>
          
          <div class="form-group mb-3">
            <label for="email" class="form-label">E-Mail-Adresse</label>
            <input 
              type="email" 
              id="email"
              v-model="email"
              class="form-control" 
              required
              placeholder="name@beispiel.de"
            />
            <div class="invalid-feedback" v-if="validationErrors.email">
              {{ validationErrors.email[0] }}
            </div>
          </div>
          
          <div class="form-group mb-3">
            <label for="password" class="form-label">Passwort</label>
            <input 
              type="password" 
              id="password" 
              v-model="password"
              class="form-control" 
              required
              placeholder="Passwort eingeben"
            />
            <div class="invalid-feedback" v-if="validationErrors.password">
              {{ validationErrors.password[0] }}
            </div>
          </div>
          
          <div class="form-group mb-4">
            <label for="password_confirmation" class="form-label">Passwort bestätigen</label>
            <input 
              type="password" 
              id="password_confirmation" 
              v-model="password_confirmation"
              class="form-control" 
              required
              placeholder="Passwort wiederholen"
            />
          </div>
          
          <div class="form-check mb-4">
            <input class="form-check-input" type="checkbox" id="terms" v-model="terms" required>
            <label class="form-check-label" for="terms">
              Ich akzeptiere die <a href="#" class="text-primary">Nutzungsbedingungen</a> und <a href="#" class="text-primary">Datenschutzrichtlinien</a>
            </label>
            <div class="invalid-feedback" v-if="validationErrors.terms">
              {{ validationErrors.terms[0] }}
            </div>
          </div>
          
          <button 
            type="submit" 
            class="btn btn-primary w-100 btn-lg" 
            :disabled="loading || !terms"
          >
            <span class="spinner-border spinner-border-sm me-2" v-if="loading"></span>
            <i class="fas fa-user-plus me-2" v-else></i>
            Konto erstellen
          </button>
        </form>
      </div>
      
      <div class="auth-footer">
        <p>Bereits registriert? <router-link to="/login">Anmelden</router-link></p>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { useToast } from 'vue-toastification';
import axios from 'axios';

export default defineComponent({
  name: 'Register',
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();
    const companyStore = useCompanyStore();
    const toast = useToast();
    
    // Refs für Template
    const name = ref('');
    const email = ref('');
    const password = ref('');
    const password_confirmation = ref('');
    const terms = ref(false);
    const loading = ref(false);
    const error = ref(null);
    const validationErrors = ref({});
    
    const register = async () => {
      loading.value = true;
      error.value = null;
      validationErrors.value = {};
      
      try {
        // Registrierungsanfrage senden
        const response = await axios.post('/api/register', {
          name: name.value,
          email: email.value,
          password: password.value,
          password_confirmation: password_confirmation.value
        });
        
        toast.success('Konto erfolgreich erstellt!');
        
        // Automatisch einloggen
        await authStore.login({
          email: email.value,
          password: password.value
        });
        
        // Firmendaten initialisieren
        await companyStore.initializeCompany();
        
        // Zur Dashboard-Seite weiterleiten
        router.push('/dashboard');
      } catch (err) {
        if (err.response) {
          // Validierungsfehler verarbeiten
          if (err.response.status === 422) {
            validationErrors.value = err.response.data.errors || {};
            error.value = 'Bitte korrigieren Sie die markierten Felder.';
          } else {
            error.value = err.response.data.message || 'Bei der Registrierung ist ein Fehler aufgetreten.';
          }
        } else {
          error.value = 'Es konnte keine Verbindung zum Server hergestellt werden.';
        }
      } finally {
        loading.value = false;
      }
    };
    
    return {
      name,
      email,
      password,
      password_confirmation,
      terms,
      loading,
      error,
      validationErrors,
      register
    };
  }
});
</script>

<style lang="scss" scoped>
.auth-card {
  width: 100%;
  max-width: 500px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  
  .auth-header {
    padding: 2.5rem 2rem;
    text-align: center;
    background: linear-gradient(135deg, var(--secondary-color) 0%, var(--primary-color) 100%);
    color: white;
    
    .logo {
      height: 60px;
      margin-bottom: 1.5rem;
      filter: brightness(0) invert(1);
    }
    
    h1 {
      font-weight: 700;
      margin-bottom: 0.5rem;
    }
    
    p {
      opacity: 0.9;
      margin-bottom: 0;
    }
  }
  
  .auth-body {
    padding: 2.5rem 2rem;
    background-color: var(--card-bg);
  }
  
  .auth-footer {
    padding: 1.5rem;
    text-align: center;
    background-color: var(--card-bg);
    border-top: 1px solid var(--border-color);
    
    p {
      margin-bottom: 0;
    }
    
    a {
      color: var(--primary-color);
      font-weight: 500;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.btn-primary {
  height: 48px;
  font-weight: 500;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(var(--primary-color), 0.35);
  }
}

.form-control {
  height: 48px;
  border-radius: 8px;
  
  &:focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 0.25rem rgba(var(--primary-color), 0.25);
  }
  
  &.is-invalid {
    border-color: var(--danger-color);
    box-shadow: none;
  }
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.invalid-feedback {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: var(--danger-color);
}
</style> 